import { useMemo } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Icon,
  Checkbox,
  Center,
  Spinner,
} from "@chakra-ui/react";
import {
  FaBox,
  FaCalendar,
  FaBolt,
  FaFileInvoice,
  FaCheckCircle,
  FaExclamationCircle,
  FaTruck,
  FaMotorcycle,
  FaUser,
} from "react-icons/fa";
import { MdAccessTime } from "react-icons/md";
import ElectricRickshawIcon from "@mui/icons-material/ElectricRickshaw";
import {
  findLatestETAWithGivenTime,
  formatDateForNotification,
  formatTime,
} from "../../../utils/dateResolvers";
import { GaraazCopyText } from "@svan-garaaz/reactsharedlib";
import { DeliveryModes, VehicleType } from "../../../types";
import DeliveryModeBadge from "../../../components/DeliveryModeBadge";
import { OrderData, EntityData } from "../types";

type SupplierData = EntityData;

const getVehicleTypeInfo = (order: OrderData) => {
  const eligibleVehicleTypes = order.orderItems
    ? order.orderItems
        .map((item) => item.eligibleVehicleType)
        .filter((val): val is number => typeof val === "number" && val > 0)
    : [];
  const maxEligibleVehicleType =
    eligibleVehicleTypes.length > 0 ? Math.max(...eligibleVehicleTypes) : 0;

  const vehicleType = (() => {
    if (order.PPDAmount === 2) return VehicleType.TWO_WHEELER;
    if (order.PPDAmount === 3) return VehicleType.THREE_WHEELER;
    return VehicleType.OTHER;
  })();

  const givenLabel =
    typeof order.PPDAmount === "number" && order.PPDAmount > 0
      ? `${order.PPDAmount}W`
      : "-";

  const eligibleLabel =
    maxEligibleVehicleType > 0 ? `${maxEligibleVehicleType}W` : "-";

  const displayLabel = `${givenLabel} / ${eligibleLabel}`;

  const { icon, color } = (() => {
    if (vehicleType === VehicleType.TWO_WHEELER) {
      return {
        icon: FaMotorcycle,
        color: "blue.500",
      };
    }
    if (vehicleType === VehicleType.THREE_WHEELER) {
      return {
        icon: ElectricRickshawIcon,
        color: "green.500",
      };
    }
    return { icon: FaTruck, color: "purple.500" };
  })();

  return { icon, color, displayLabel };
};

// InvoiceInfo Component
interface InvoiceInfoProps {
  invoiceInfo?: {
    invoiceMediaId?: { $oid: string };
    invoiceNo?: string;
    invoiceAmount?: string;
    invoiceDate?: string;
    noOfItems?: number;
    totalQtyOfItems?: number;
    invoiceStatus?: string;
  };
  challanInfo?: {
    challanMediaId?: { $oid: string };
    challanNo?: string;
    challanAmount?: string;
    challanDate?: string;
  };
}
const InvoiceInfo: React.FC<InvoiceInfoProps> = ({
  invoiceInfo,
  challanInfo,
}) => {
  const hasInvoice = invoiceInfo && invoiceInfo.invoiceNo;
  const hasChallan = challanInfo && challanInfo.challanNo;

  const getStatusColor = (status?: string) => {
    switch (status?.toUpperCase()) {
      case "PAID":
        return { color: "green.600", bg: "green.50", borderColor: "green.200" };
      case "UNPAID":
        return { color: "red.600", bg: "red.50", borderColor: "red.200" };
      default:
        return { color: "gray.600", bg: "gray.50", borderColor: "gray.200" };
    }
  };

  return (
    <HStack spacing={0} align="start" width="100%">
      {!hasInvoice && !hasChallan && (
        <Text fontSize="sm" color="gray.400" fontStyle="italic">
          No invoice or Challan
        </Text>
      )}
      {hasInvoice && (
        <VStack align="start" spacing={1} flex={1} minW="0" pr={2}>
          <>
            <HStack spacing={1} align="center">
              <Text fontSize="sm" fontWeight="600" color="gray.700">
                {invoiceInfo.invoiceNo}
              </Text>
              <GaraazCopyText
                text={invoiceInfo.invoiceNo!}
                popOverMessage={"Invoice No Copied"}
                key={invoiceInfo.invoiceNo}
              />
            </HStack>
            {invoiceInfo.invoiceAmount && (
              <Text fontSize="sm" color="gray.600">
                ₹{parseFloat(invoiceInfo.invoiceAmount).toLocaleString()}
              </Text>
            )}
            {invoiceInfo.invoiceDate && (
              <Text fontSize="xs" color="gray.500">
                {invoiceInfo.invoiceDate}
              </Text>
            )}

            {invoiceInfo.invoiceStatus && (
              <Badge
                px={2}
                py={0.5}
                borderRadius="full"
                fontSize="xs"
                fontWeight="600"
                color={getStatusColor(invoiceInfo.invoiceStatus).color}
                bg={getStatusColor(invoiceInfo.invoiceStatus).bg}
                border="1px"
                borderColor={
                  getStatusColor(invoiceInfo.invoiceStatus).borderColor
                }
                textTransform="none"
              >
                <HStack spacing={1} align="center">
                  <Icon
                    as={
                      invoiceInfo.invoiceStatus.toUpperCase() === "PAID"
                        ? FaCheckCircle
                        : FaExclamationCircle
                    }
                    boxSize={2.5}
                  />
                  <Text>{invoiceInfo.invoiceStatus}</Text>
                </HStack>
              </Badge>
            )}
          </>
        </VStack>
      )}

      {hasInvoice && hasChallan && (
        <Box width="1px" bg="gray.300" alignSelf="stretch" minH="100%" />
      )}

      {hasChallan && (
        <VStack align="start" spacing={1} flex={1} minW="0" pl={2}>
          <>
            <Text fontSize="sm" fontWeight="900" color="gray.700">
              Challan Details
            </Text>
            <HStack spacing={1} align="center">
              <Text fontSize="sm" fontWeight="600" color="gray.700">
                {challanInfo.challanNo}
              </Text>
              <GaraazCopyText
                text={challanInfo.challanNo!}
                popOverMessage={"Challan No Copied"}
                key={challanInfo.challanNo}
              />
            </HStack>
            {challanInfo.challanAmount && (
              <Text fontSize="sm" color="gray.600">
                ₹{parseFloat(challanInfo.challanAmount).toLocaleString()}
              </Text>
            )}
            {challanInfo.challanDate && (
              <Text fontSize="xs" color="gray.500">
                {challanInfo.challanDate}
              </Text>
            )}
          </>
        </VStack>
      )}
    </HStack>
  );
};

export interface OrdersCardProps {
  sortedOrders: OrderData[];
  supplier: SupplierData;
  customerId?: string;
  selectedOrders?: string[];
  onOrderSelectionChange?: (
    orderNo: string,
    isSelected: boolean,
    customerId?: string,
    customerName?: string,
    supplierId?: string,
    supplierName?: string,
    deliveryMode?: DeliveryModes,
    orderId?: string,
    orderData?: OrderData,
  ) => void;
  ordersLoading: Boolean;
  orders: OrderData[];
  showRiderDetail?: boolean;
}

export const OrdersCard = ({
  sortedOrders,
  supplier,
  customerId,
  selectedOrders = [],
  onOrderSelectionChange,
  ordersLoading,
  orders,
  showRiderDetail = false,
}: OrdersCardProps) => {
  const vehicleTypeInfoMap = useMemo(() => {
    const map = new Map<string, ReturnType<typeof getVehicleTypeInfo>>();
    sortedOrders.forEach((order) => {
      map.set(order.orderNo, getVehicleTypeInfo(order));
    });
    return map;
  }, [sortedOrders]);

  return (
    <Box p={0}>
      {ordersLoading ? (
        <Center py={8}>
          <VStack spacing={3}>
            <Spinner size="md" color="blue.500" />
            <Text color="gray.600" fontSize="sm">
              Loading orders for {supplier.businessName}...
            </Text>
          </VStack>
        </Center>
      ) : orders.length === 0 ? (
        <Center py={8}>
          <VStack spacing={2}>
            <Box p={3} bg="gray.100" borderRadius="full">
              <Icon as={FaBox} boxSize={4} color="gray.400" />
            </Box>
            <Text color="gray.500" fontSize="sm">
              No orders found for this supplier
            </Text>
          </VStack>
        </Center>
      ) : (
        <Box overflow="hidden" borderRadius="lg">
          <Box maxH="60" overflowY="auto" overflowX="auto">
            <Table
              variant="unstyled"
              size="sm"
              width="100%"
              sx={{
                width: "100%",
              }}
            >
              <Thead
                bg="linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)"
                position="sticky"
                top="0"
                zIndex="1"
              >
                <Tr>
                  <Th
                    px={4}
                    py={4}
                    borderBottom="2px"
                    borderColor="gray.200"
                    width="10%"
                  >
                    <Checkbox
                      isChecked={
                        sortedOrders.length > 0 &&
                        sortedOrders.every((order) =>
                          selectedOrders.includes(order.orderNo),
                        )
                      }
                      isIndeterminate={
                        sortedOrders.some((order) =>
                          selectedOrders.includes(order.orderNo),
                        ) &&
                        !sortedOrders.every((order) =>
                          selectedOrders.includes(order.orderNo),
                        )
                      }
                      onChange={(e) => {
                        sortedOrders.forEach((order) => {
                          onOrderSelectionChange?.(
                            order.orderNo,
                            e.target.checked,
                            customerId,
                            order.customer.name,
                            supplier._id,
                            supplier.businessName,
                            order.deliveryMode as DeliveryModes,
                            order._id,
                            order,
                          );
                        });
                      }}
                    />
                  </Th>
                  <Th px={4} py={4} borderBottom="2px" borderColor="gray.200">
                    <HStack spacing={2}>
                      <Text
                        fontSize="xs"
                        fontWeight="700"
                        color="gray.800"
                        letterSpacing="0.025em"
                      >
                        # Order Number
                      </Text>
                    </HStack>
                  </Th>

                  <Th px={4} py={4} borderBottom="2px" borderColor="gray.200">
                    <HStack spacing={2}>
                      <Icon as={FaFileInvoice} boxSize={3} color="purple.500" />
                      <Text
                        fontSize="xs"
                        fontWeight="700"
                        color="gray.800"
                        letterSpacing="0.025em"
                      >
                        Invoice Info
                      </Text>
                    </HStack>
                  </Th>

                  <Th px={4} py={4} borderBottom="2px" borderColor="gray.200">
                    <HStack spacing={2}>
                      <Icon as={FaTruck} boxSize={3} color="purple.500" />
                      <Text
                        fontSize="xs"
                        fontWeight="700"
                        color="gray.800"
                        letterSpacing="0.025em"
                      >
                        Delivery Mode
                      </Text>
                    </HStack>
                  </Th>

                  <Th px={4} py={4} borderBottom="2px" borderColor="gray.200">
                    <HStack spacing={2}>
                      <Icon as={FaMotorcycle} boxSize={3} color="blue.500" />
                      <Text
                        fontSize="xs"
                        fontWeight="700"
                        color="gray.800"
                        letterSpacing="0.025em"
                      >
                        Given/Eligible VT
                      </Text>
                    </HStack>
                  </Th>

                  <Th px={4} py={4} borderBottom="2px" borderColor="gray.200">
                    <HStack spacing={2}>
                      <Icon as={FaCalendar} boxSize={3} color="green.500" />
                      <Text
                        fontSize="xs"
                        fontWeight="700"
                        color="gray.800"
                        letterSpacing="0.025em"
                      >
                        Date
                      </Text>
                    </HStack>
                  </Th>
                  <Th px={4} py={4} borderBottom="2px" borderColor="gray.200">
                    <HStack spacing={2}>
                      <Icon as={FaBox} boxSize={3} color="orange.500" />
                      <Text
                        fontSize="xs"
                        fontWeight="700"
                        color="gray.800"
                        letterSpacing="0.025em"
                      >
                        Order Items
                      </Text>
                    </HStack>
                  </Th>
                  <Th px={4} py={4} borderBottom="2px" borderColor="gray.200">
                    <HStack spacing={2}>
                      <Icon
                        as={MdAccessTime}
                        boxSize={3.5}
                        color="orange.500"
                      />
                      <Text
                        fontSize="xs"
                        fontWeight="700"
                        color="gray.800"
                        letterSpacing="0.025em"
                        title="Orders are sorted by earliest CPT first"
                      >
                        CPT
                      </Text>
                    </HStack>
                  </Th>
                  {showRiderDetail && (
                    <Th px={4} py={4} borderBottom="2px" borderColor="gray.200">
                      <HStack spacing={2}>
                        <Icon as={FaUser} boxSize={3} color="purple.500" />
                        <Text
                          fontSize="xs"
                          fontWeight="700"
                          color="gray.800"
                          letterSpacing="0.025em"
                        >
                          Rider Detail
                        </Text>
                      </HStack>
                    </Th>
                  )}
                </Tr>
              </Thead>
              <Tbody>
                {sortedOrders.map((order) => {
                  const isDisabled = false;

                  const etaText = () => {
                    const eta = findLatestETAWithGivenTime(
                      order?.orderETA ?? [],
                    );
                    const latestTime = eta?.latestTime;
                    if (!latestTime) return "-";

                    const latestDate = new Date(latestTime);
                    if (Number.isNaN(+latestDate)) return "-";

                    return `${formatTime(
                      latestDate,
                    )} - ${formatDateForNotification(latestDate)}`;
                  };

                  return (
                    <Tr
                      key={order.orderNo}
                      bg="white"
                      _hover={{
                        bg: "gray.50",
                        transform: "translateX(2px)",
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
                      }}
                      transition="all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
                      borderBottom="1px"
                      borderColor="gray.100"
                    >
                      <Td px={4} py={4}>
                        <Checkbox
                          isChecked={selectedOrders.includes(order.orderNo)}
                          onChange={(e) => {
                            onOrderSelectionChange?.(
                              order.orderNo,
                              e.target.checked,
                              customerId,
                              order.customer.name,
                              supplier._id,
                              supplier.businessName,
                              order.deliveryMode as DeliveryModes,
                              order._id,
                              order,
                            );
                          }}
                        />
                      </Td>
                      <Td px={4} py={4}>
                        <VStack align="start" spacing={1}>
                          <Box
                            key={order.orderNo}
                            flexDirection={"row"}
                            display={"flex"}
                            gap={1}
                            mt={1}
                          >
                            <Text fontSize="sm" color="gray.600" mt={-0.5}>
                              {order.orderNo}
                            </Text>
                            {order.isUrgent === true && (
                              <Icon
                                as={FaBolt}
                                boxSize={3}
                                color="orange.500"
                                opacity={0.9}
                              />
                            )}
                            <GaraazCopyText
                              text={order.orderNo}
                              popOverMessage={"Copied"}
                              key={order.orderNo}
                            />
                          </Box>
                        </VStack>
                      </Td>

                      <Td px={4} py={4}>
                        <InvoiceInfo
                          invoiceInfo={order.invoiceInfo}
                          challanInfo={order.challanInfo}
                        />
                      </Td>

                      <Td px={4} py={4}>
                        <DeliveryModeBadge
                          mode={order.deliveryMode as DeliveryModes}
                        />
                      </Td>
                      <Td px={4} py={4}>
                        {(() => {
                          const { icon, color, displayLabel } =
                            vehicleTypeInfoMap.get(order.orderNo) ||
                            getVehicleTypeInfo(order);
                          return (
                            <HStack spacing={2}>
                              <Icon as={icon} color={color} boxSize={4} />
                              <Text
                                fontSize="sm"
                                color="gray.700"
                                fontWeight="600"
                              >
                                {displayLabel}
                              </Text>
                            </HStack>
                          );
                        })()}
                      </Td>
                      <Td px={4} py={4}>
                        <Text fontSize="sm" color="gray.800">
                          {new Date(order.orderDate).toLocaleDateString()}
                        </Text>
                      </Td>
                      <Td px={4} py={4}>
                        <Text fontSize="sm" color="gray.700" fontWeight="500">
                          {order.totalItems} Items, {order.totalQuantity} qty
                        </Text>
                      </Td>
                      <Td px={4} py={4}>
                        <Text fontSize="sm" color="gray.700" fontWeight="500">
                          {etaText()}
                        </Text>
                      </Td>
                      {showRiderDetail && (
                        <Td px={4} py={4}>
                          {order.dispatchRider?.name ||
                          order.dispatchRider?.phone ? (
                            <VStack spacing={0.5} align="start">
                              <Text
                                fontSize="xs"
                                color="gray.700"
                                fontWeight="medium"
                              >
                                {order.dispatchRider.name || "-"}
                              </Text>
                              {order.dispatchRider.phone && (
                                <Text fontSize="xs" color="blue.500">
                                  {order.dispatchRider.phone}
                                </Text>
                              )}
                              {order.dispatchRider.vehicleNumber && (
                                <Text fontSize="xs" color="gray.500">
                                  {order.dispatchRider.vehicleNumber}
                                </Text>
                              )}
                            </VStack>
                          ) : (
                            <Text fontSize="xs" color="gray.400">
                              -
                            </Text>
                          )}
                        </Td>
                      )}
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>
        </Box>
      )}
    </Box>
  );
};
